import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppMainOverlayHost } from '../../../context/AppMainOverlayContext';
import { SopAPI } from '../../../utils/api';
import { BlockRenderer } from '../renderer';
import { findBlock } from '../utils/blockUtils';
import { deepBlockIdFor, rangeBetween } from '../utils/blockSelectionUtils';
import {
  isPickableSnippetBlock,
  snippetPreviewText,
  snippetTypeLabel,
  truncatePreview,
} from '../utils/snippetUtils';
import { renderIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';

const noop = () => {};

/**
 * Full-app read-only doc viewer for choosing a snippet range.
 * Click one block, or click a second to select everything between.
 */
export const SnippetSourcePickerOverlay = ({
  docMeta,
  onChoose,
  onBack,
  onClose,
}) => {
  const overlayHost = useAppMainOverlayHost();
  const [body, setBody] = useState(null);
  const [fullDoc, setFullDoc] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [anchorId, setAnchorId] = useState(null);
  const [focusId, setFocusId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBody(null);
    setLoadError('');
    setAnchorId(null);
    setFocusId(null);
    SopAPI.get(docMeta.id)
      .then((doc) => {
        if (cancelled) return;
        setFullDoc(doc);
        setBody(Array.isArray(doc?.body) ? doc.body : []);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message || 'Failed to load doc');
      });
    return () => {
      cancelled = true;
    };
  }, [docMeta.id]);

  const selectedIds = useMemo(() => {
    if (!body || !anchorId) return [];
    if (!focusId || focusId === anchorId) return [anchorId];
    return rangeBetween(body, anchorId, focusId) || [anchorId];
  }, [body, anchorId, focusId]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedBlocks = useMemo(() => {
    if (!body) return [];
    return selectedIds.map((id) => findBlock(body, id)).filter(Boolean);
  }, [body, selectedIds]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const resolveClickTarget = useCallback(
    (target) => {
      if (!body) return null;
      let id = deepBlockIdFor(target);
      if (!id) return null;
      // Walk up past layout containers to a pickable block
      let guard = 0;
      while (id && guard < 20) {
        guard += 1;
        const block = findBlock(body, id);
        if (!block) return null;
        if (isPickableSnippetBlock(block)) return id;
        // Unpickable (row/column/snippet): try parent via path
        const path = [];
        const walk = (list, trail) => {
          for (const b of list) {
            if (b.id === id) {
              path.push(...trail, b.id);
              return true;
            }
            if (b.children?.length && walk(b.children, [...trail, b.id])) {
              return true;
            }
          }
          return false;
        };
        walk(body, []);
        if (path.length < 2) return null;
        id = path[path.length - 2];
      }
      return null;
    },
    [body]
  );

  const handleBodyClick = (e) => {
    const id = resolveClickTarget(e.target);
    if (!id) return;
    e.preventDefault();
    e.stopPropagation();

    if (!anchorId || (anchorId && focusId)) {
      // Fresh selection (or restart after a completed range)
      setAnchorId(id);
      setFocusId(null);
      return;
    }
    // Second click extends to a range
    setFocusId(id);
  };

  const handleChoose = () => {
    if (!selectedBlocks.length || !fullDoc) return;
    const first = selectedBlocks[0];
    onChoose({
      sourceDocId: fullDoc.id,
      sourceBlockIds: selectedIds,
      sourceBlockId: selectedIds[0], // legacy alias
      sourceDocTitle: fullDoc.title || docMeta.title || 'Untitled Doc',
      sourceDocIcon: fullDoc.icon ?? docMeta.icon,
      sourceBlockType: first.type,
      previewText: truncatePreview(snippetPreviewText(first)),
      sourceBlocks: selectedBlocks,
    });
  };

  const selectionHint = !anchorId
    ? 'Click a block to select it'
    : !focusId
      ? 'Click another block to select a range, or Choose for just this one'
      : `${selectedIds.length} block${selectedIds.length === 1 ? '' : 's'} selected`;

  const panel = (
    <div className="snippet-source-picker" role="dialog" aria-modal="true">
      <button
        type="button"
        className="snippet-source-picker__backdrop"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="snippet-source-picker__panel">
        <div className="snippet-source-picker__header">
          <div className="snippet-source-picker__title">
            <span className="snippet-source-picker__icon">
              {renderIcon(fullDoc?.icon ?? docMeta.icon, {
                size: '18px',
                fallback: <span>📄</span>,
              })}
            </span>
            <span>{fullDoc?.title || docMeta.title || 'Untitled Doc'}</span>
          </div>
          <div className="snippet-source-picker__actions">
            <button
              type="button"
              className="snippet-source-picker__btn"
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="button"
              className="snippet-source-picker__btn snippet-source-picker__btn--ghost"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="snippet-source-picker__btn snippet-source-picker__btn--primary"
              disabled={!selectedBlocks.length}
              onClick={handleChoose}
            >
              Choose
            </button>
          </div>
        </div>

        <div className="snippet-source-picker__hint">{selectionHint}</div>

        <div
          className="snippet-source-picker__body doc-editor doc-editor--readonly"
          onClick={handleBodyClick}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && ['c', 'a'].includes(e.key.toLowerCase())) {
              return;
            }
            if (e.key === 'Escape' || e.key === 'Tab') return;
            e.preventDefault();
          }}
          onBeforeInput={(e) => e.preventDefault()}
        >
          {loadError && (
            <div className="snippet-source-picker__empty">{loadError}</div>
          )}
          {body === null && !loadError && (
            <div className="snippet-source-picker__empty">Loading…</div>
          )}
          {body && (
            <div className="doc-editor-blocks">
              {body.map((block, i) => (
                <div
                  key={block.id}
                  className={[
                    'snippet-source-picker__block',
                    selectedIdSet.has(block.id)
                      ? 'snippet-source-picker__block--selected'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-snippet-pick-root={block.id}
                >
                  <BlockRenderer
                    block={block}
                    prevType={body[i - 1]?.type}
                    nextType={body[i + 1]?.type}
                    onUpdate={noop}
                    onInsertAfter={noop}
                    onUpdateAndInsert={noop}
                    onDelete={noop}
                    onMergeWithPrevious={noop}
                    onMergeWithNext={noop}
                    onNavigate={noop}
                    onEscapeContainer={noop}
                    selectedBlockId={null}
                    multiSelectedIds={
                      // Highlight nested selected siblings inside containers
                      selectedIds
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedBlocks.length > 0 && (
          <div className="snippet-source-picker__footer">
            <span className="snippet-source-picker__footer-meta">
              {selectedBlocks.length === 1
                ? snippetTypeLabel(selectedBlocks[0])
                : `${selectedBlocks.length} blocks`}
              {' · '}
              {truncatePreview(snippetPreviewText(selectedBlocks[0]), 60)}
            </span>
            <button
              type="button"
              className="snippet-source-picker__btn snippet-source-picker__btn--primary"
              onClick={handleChoose}
            >
              Choose
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(panel, overlayHost || document.body);
};
